import React, { useEffect, useState } from "react";
import getSymbolFromCurrency from "currency-symbol-map";
import { isMobile } from "react-device-detect";
import {
  parseAssetTypeInt,
  parseCountryCodeInt,
  getActualTime,
} from "../../../../utils";
import AssetInfoTrial from "./AssetInfoTrial";
import Skeleton from "react-loading-skeleton";
import { FaRegSadCry } from "react-icons/fa";
import { getFollowButton } from "../../../../pages/FollowingList";
import useIcons from "../../../../hooks/useIcons";
import useFollowingAssets from "../../../../hooks/useFollowingAssets";
import useUserPreferences from "../../../../hooks/useUserPreferences";
import { useUser } from "../../../../hooks/useUser";
import { Card } from "react-bootstrap";
import ProfilePictureStories from "./ProfilePictureStories";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.min.css";
import "swiper/swiper.min.css";
import SwiperCore, { Navigation, Pagination, Scrollbar, A11y } from "swiper";
import useWindowDimensions from "../../../../hooks/useWindowDimension";
SwiperCore.use([Navigation, Pagination, Scrollbar, A11y]);

/**
 * Profile card about an asset containing prices, stories and others...
 */
export function AssetProfile({
  assetProfileData,
  analysisData,
  show,
  billingState,
}) {
  const [isUserFollowing, setIsUserFollowing] = useState(null);
  const { addStateAsset, removeStateAsset } = useFollowingAssets();
  const { addAsset, removeAsset } = useUserPreferences();
  const [qntFollowers, setQntFollowers] = useState(0);
  const { getURI } = useIcons();
  const { getAssets } = useUser();
  const { width } = useWindowDimensions();
  //Determines if the user actually follows the asset.
  useEffect(() => {
    let fetchData = async () => {
      let userAccountAssets = await getAssets();
      if (userAccountAssets) {
        let actualAssetId = assetProfileData.encoded_id;
        //Check if the user follow the asset and update the state.
        let userFollow = false;
        userAccountAssets.forEach((assetId) => {
          //If the actual asset id is the same as one in user account assets
          if (actualAssetId === assetId) {
            userFollow = true;
          }
        });
        setIsUserFollowing(userFollow);
        setQntFollowers(assetProfileData.followers_qnt);
      }
    };
    if (assetProfileData && analysisData) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetProfileData, analysisData]);

  //Handle the click on "Follow"
  const handleFollowButton = async () => {
    let assetId = assetProfileData.encoded_id;

    if (isUserFollowing === true) {
      setIsUserFollowing(null); //Make a loading button
      //Unfollow this asset.
      let request = await removeAsset(assetId);
      if (request) {
        let { status } = request;
        if (status === 1) {
          //All done
          removeStateAsset(assetId);
          setIsUserFollowing(false);
          setQntFollowers(qntFollowers - 1);
        }
      } else {
        //Error on request
        setIsUserFollowing(true);
      }
    } else if (isUserFollowing === false) {
      //Follow this asset.
      setIsUserFollowing(null); //Make a loading button
      let request = await addAsset(assetId);
      if (request) {
        let { status } = request;
        if (status === 1) {
          //All done.
          setIsUserFollowing(true);
          setQntFollowers(qntFollowers + 1);
          addStateAsset({
            name: assetProfileData.name,
            country_code: assetProfileData.country_code,
            currency_symbol: assetProfileData.currency_symbol,
            encoded_id: assetProfileData.encoded_id,
            exchange: assetProfileData.exchange,
            icon: assetProfileData.icon,
            status: 1,
            symbol: assetProfileData.symbol,
            type: assetProfileData.type,
          });
        }
      } else {
        //Error on request.
        setIsUserFollowing(false);
      }
    }
  };

  if (!assetProfileData) {
    return <></>;
  }

  const newsData = analysisData && analysisData.analysisResults.News;

  const assetIcon = getURI(assetProfileData && assetProfileData.icon);

  const getChangeInterval = () => {
    if (analysisData) {
      switch (analysisData.analysisResults.Informations.investorType) {
        case 0:
          return 15;
        case 1:
          return 30;
        case 2:
          return 180;
        default:
          return 0;
      }
    }
    return 0;
  };

  const hoursAndMinutes = getActualTime();
  let pricePrimaryColor = "#424242";

  const historicalDataRequestStatus =
    analysisData && analysisData.requestsResults.Historical;

  const historicalPrices =
    analysisData && analysisData.analysisResults.Historical;

  const fairValueDataRequestStatus =
    analysisData && analysisData.requestsResults.FairValue;

  const fairValuePrices =
    analysisData && analysisData.analysisResults.FairValue;

  let lastPrice;
  let summaryVar;
  let priceTarget;
  let insecurity;

  const disablePremium = billingState?.totalRemaining === 0;

  //FairValue
  if (fairValueDataRequestStatus === 7) {
    priceTarget = fairValuePrices.Target;
    insecurity = fairValuePrices.Insecurity;
  }

  //Historical
  if (historicalDataRequestStatus === 7) {
    lastPrice = historicalPrices.Last.PriceString;
    summaryVar = `${String(historicalPrices.Summary.Var.toFixed(2)).replace(
      ".",
      ","
    )}%`;

    //Define color to negative or positive variations
    if (summaryVar) {
      if (summaryVar.includes("-")) {
        pricePrimaryColor = "#e53935";
      } else {
        pricePrimaryColor = "#66bb6a";
      }
    }
    //if an problem happens with the price.
    if (lastPrice === null || lastPrice === 0) {
      lastPrice = "⚠️";
    }
    if (summaryVar === null || summaryVar === 0) {
      summaryVar = "⚠️";
    }
  } else if (historicalDataRequestStatus === 6) {
    lastPrice = "⚠️";
    summaryVar = "⚠️";
  }

  if (!show) {
    return <></>;
  } else if (assetProfileData.status === 1) {
    const countryName = parseCountryCodeInt(assetProfileData.country_code);
    let currencySymbol = getSymbolFromCurrency(
      assetProfileData.currency_symbol
    );
    //If no currency symbol encountered, then keep it empty string.
    if (!currencySymbol) {
      currencySymbol = "";
    }

    return (
      <div className="row row-sm">
        <div className="col-md">
          <div className="card card-minimal-four">
            <div className="card-header" id="analysis-step-8">
              <div className="media">
                <ProfilePictureStories
                  profilePictureSrc={assetIcon}
                  newsData={newsData}
                  assetType={assetProfileData.type}
                  assetName={assetProfileData.name}
                />

                <div className="media-body">
                  <h2 className="az-content-title-minimal">
                    {assetProfileData.name}
                  </h2>
                  <span>
                    Ticker:
                    <span
                      className="badge badge-pill badge-secondary"
                      style={{ marginLeft: 3 }}
                    >
                      {assetProfileData.symbol}
                    </span>
                  </span>
                  <p>
                    Tipo:
                    <span
                      className="tx-normal"
                      style={{
                        color: "var(--text-6)",
                        marginLeft: 3,
                        marginRight: 3,
                      }}
                    >
                      {parseAssetTypeInt(assetProfileData.type)}
                    </span>
                    {assetProfileData.exchange && (
                      <span
                        className="tx-normal"
                        style={{ color: "var(--text-6)" }}
                      >
                        {"- " + assetProfileData.exchange}
                      </span>
                    )}
                  </p>
                  {countryName && (
                    <p>
                      País:
                      <span
                        className="tx-normal"
                        style={{ color: "var(--text-6)", marginLeft: 3 }}
                      >
                        {countryName}
                      </span>
                    </p>
                  )}
                  {
                    //Following button
                    analysisData && (
                      <div
                        className="mg-t-5 wd-20"
                        onClick={handleFollowButton}
                      >
                        {getFollowButton(isUserFollowing)}
                      </div>
                    )
                  }
                  {/* Desktop */}
                  {width >= 768 && !disablePremium && (
                    <div>
                      <Card
                        id="analysis-step-1"
                        className="pd-20 mg-t-10 mg-b-10"
                        style={{ borderRadius: 15, width: "fit-content" }}
                      >
                        <div className="d-flex" style={{ gap: 20 }}>
                          <div className="flex-column">
                            <h6
                              className="update_text tx-13 tx-inverse tx-semibold mg-b-0"
                              style={{ color: pricePrimaryColor }}
                            >
                              {lastPrice ? (
                                currencySymbol + " " + lastPrice
                              ) : (
                                <Skeleton width={35} />
                              )}
                            </h6>
                            <span className="d-block tx-11 text-muted">
                              Preço
                            </span>
                          </div>

                          {qntFollowers > 50 && (
                            <div className="flex-column">
                              <h6 className="tx-13 tx-inverse tx-semibold mg-b-0 text-center">
                                {new Intl.NumberFormat("pt-BR", {
                                  notation: "compact",
                                  compactDisplay: "short",
                                }).format(qntFollowers)}
                              </h6>
                              <span className="d-block tx-11 text-muted">
                                Seguidores
                              </span>
                            </div>
                          )}

                          <div className="flex-column">
                            <h6 className="tx-13 tx-inverse tx-semibold mg-b-0">
                              {assetProfileData.currency_symbol}
                            </h6>
                            <span className="d-block tx-11 text-muted">
                              Moeda
                            </span>
                          </div>

                          <div className="flex-column">
                            <h6
                              className="update_text tx-13 tx-inverse tx-semibold mg-b-0"
                              style={{ color: pricePrimaryColor }}
                            >
                              {summaryVar ? (
                                summaryVar.includes("-") ? (
                                  summaryVar
                                ) : (
                                  "+" + summaryVar
                                )
                              ) : (
                                <Skeleton width={40} />
                              )}
                            </h6>
                            <span className="d-block tx-11 text-muted">
                              Mudança (
                              {getChangeInterval()
                                ? getChangeInterval() + "D"
                                : "..."}
                              )
                            </span>
                          </div>

                          {priceTarget ? (
                            <div className="flex-column">
                              <h6 className="tx-13 tx-inverse tx-semibold mg-b-0">
                                {currencySymbol} {priceTarget.toFixed(2)}
                              </h6>
                              <span className="d-block tx-11 text-muted">
                                Preço alvo AI
                              </span>
                            </div>
                          ) : (
                            <></>
                          )}

                          {insecurity &&
                          insecurity !== "UNKNOWN_UNCERTAINTY" ? (
                            <div className="flex-column">
                              <h6 className="tx-13 tx-inverse tx-semibold mg-b-0">
                                {insecurtyToSecurity(insecurity)}
                              </h6>
                              <span className="d-block tx-11 text-muted">
                                Segurança
                              </span>
                            </div>
                          ) : (
                            <></>
                          )}
                        </div>
                      </Card>

                      {analysisData && !isMobile ? (
                        <span
                          className="update_text d-block tx-12"
                          style={{ textAlign: "right" }}
                        >
                          Ultima atualização:{" "}
                          {analysisData ? "Hoje às " + hoursAndMinutes : null}
                        </span>
                      ) : null}
                    </div>
                  )}
                  {width >= 768 && disablePremium && (
                    <div>
                      <AssetInfoTrial />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile */}
            {width < 768 && !disablePremium && (
              <div className="card-body">
                {
                  // MOBILE!
                  //if is an mobile, let's show the price change inside an card-body.
                  <div className="d-flex justify-content-center">
                    <Card
                      id="analysis-step-1"
                      className="pd-30 pd-b-0 mg-t-10"
                      style={{
                        borderRadius: 15,
                        maxWidth: "90%",
                        paddingBottom: 0,
                      }}
                    >
                      {
                        <Swiper
                          spaceBetween={20}
                          className="swiper-slide"
                          slidesPerView={String(lastPrice).length > 5 ? 2 : 3}
                          pagination={{ type: "bullets" }}
                        >
                          <SwiperSlide className="mg-b-60 text-center ">
                            <h6
                              className="update_text tx-13 tx-inverse tx-semibold mg-b-0"
                              style={{
                                color: pricePrimaryColor,
                              }}
                            >
                              {lastPrice ? (
                                currencySymbol + " " + lastPrice
                              ) : (
                                <Skeleton width={35} />
                              )}
                            </h6>
                            <span className="d-block tx-11 text-muted">
                              Preço
                            </span>
                          </SwiperSlide>

                          <SwiperSlide className="mg-b-40  text-center ">
                            <p className="tx-13 tx-inverse tx-semibold mg-b-0">
                              {assetProfileData.currency_symbol}
                            </p>
                            <span className="d-block tx-11 text-muted">
                              Moeda
                            </span>
                          </SwiperSlide>

                          <SwiperSlide className="mg-b-40 text-center">
                            <h6
                              className="update_text tx-13 tx-inverse tx-semibold mg-b-0"
                              style={{ color: pricePrimaryColor }}
                            >
                              {summaryVar ? (
                                summaryVar.includes("-") ? (
                                  summaryVar
                                ) : (
                                  "+" + summaryVar
                                )
                              ) : (
                                <Skeleton width={40} />
                              )}
                            </h6>
                            <span className="d-block tx-11 text-muted">
                              Mudança (
                              {getChangeInterval()
                                ? getChangeInterval() + "D"
                                : "..."}
                              )
                            </span>
                          </SwiperSlide>

                          {qntFollowers > 50 && (
                            <SwiperSlide className="mg-b-40 text-center">
                              <div className="flex-column">
                                <h6 className="tx-13 tx-inverse tx-semibold mg-b-0">
                                  {new Intl.NumberFormat("pt-BR", {
                                    notation: "compact",
                                    compactDisplay: "short",
                                  }).format(qntFollowers)}
                                </h6>
                                <span className="d-block tx-11 text-muted">
                                  Seguidores
                                </span>
                              </div>{" "}
                            </SwiperSlide>
                          )}

                          {priceTarget ? (
                            <SwiperSlide className="mg-b-40 text-center">
                              <div className="flex-column">
                                <h6 className="tx-13 tx-inverse tx-semibold mg-b-0">
                                  {currencySymbol} {priceTarget.toFixed(2)}
                                </h6>
                                <span className="d-block tx-11 text-muted">
                                  Preço alvo AI
                                </span>
                              </div>{" "}
                            </SwiperSlide>
                          ) : (
                            <></>
                          )}

                          {insecurity &&
                          insecurity !== "UNKNOWN_UNCERTAINTY" ? (
                            <SwiperSlide className="mg-b-40 text-center">
                              <div className="flex-column">
                                <h6 className="tx-13 tx-inverse tx-semibold mg-b-0">
                                  {insecurtyToSecurity(insecurity)}
                                </h6>
                                <span className="d-block tx-11 text-muted">
                                  Segurança
                                </span>
                              </div>
                            </SwiperSlide>
                          ) : (
                            <></>
                          )}
                        </Swiper>
                      }
                    </Card>
                  </div>
                }
                {analysisData ? (
                  <span
                    className="update_text d-block tx-12 mg-r-5 mg-b-5 mg-t-10"
                    style={{ textAlign: "right" }}
                  >
                    Ultima atualização:{" "}
                    {analysisData ? "Hoje às " + hoursAndMinutes : null}
                  </span>
                ) : null}
              </div>
            )}
            {width < 768 && disablePremium && (
              <div className="card-body">
                <div className="d-flex justify-content-center mg-b-10">
                  <AssetInfoTrial />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  } else if (assetProfileData.status === 0) {
    return (
      <div className="row row-sm">
        <div className="col-md">
          <div className="card card-minimal-four">
            <div className="card-header">
              <div className="media">
                <FaRegSadCry size={50} />
                <div className="media-body">
                  <h2 className="az-content-title-minimal">Cadê a Teté!?</h2>
                  <span>
                    Desculpe, o ativo não foi encontrado ou removido da
                    plataforma.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    return <></>;
  }
}

function insecurtyToSecurity(insecurty) {
  switch (insecurty) {
    case "LOW":
      return "ALTA";
    case "MEDIUM":
      return "MÉDIA";
    case "HIGH":
      return "BAIXA";
    default:
      return "";
  }
}

export const AssetProfileMemo = React.memo(AssetProfile);
